import { Box } from '@/components/sgBootstrapReactWrapper';
import type { Person } from '@/model/Person';
import { setPersonIdDisplayedInDrawer } from '@/store/persons/slice';
import { isDefined, isNotDefined } from '@/utils/assertions';
import { formatCountryName } from '@/utils/country';
import { compareDates, formatDate } from '@/utils/date';
import type { ColDef, ValueFormatterFunc } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useMemo } from 'react';
import { FormattedMessage, type IntlFormatters, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { AGGridWrapper } from '../common/AGGridWrapper';
import { AmlLevelBadge } from './AmlLevelBadge';
import { PersonCard } from './PersonCard';

const NB_DISPLAYED_RESULTS__LISTMODE = 8;

type Props = {
  // list : display all elements as a list of cards, will only display first 10 of them
  // table: will use aggrid to display results
  displayMode: 'list' | 'table';
  persons: Person[];
  emphasizedText?: string;
};

export const PersonsList = ({ displayMode, persons, emphasizedText = '' }: Props) => {
  const { $t, locale } = useIntl();

  const dispatch = useDispatch();

  const columnDefs = useMemo(() => generateColumnDefinitions($t, locale), [$t, locale]);

  const displayedPersons: Person[] = useMemo(
    () => (displayMode === 'table' ? persons : persons.slice(0, NB_DISPLAYED_RESULTS__LISTMODE)),
    [displayMode, persons],
  );

  if (displayedPersons.length === 0) {
    return (
      <div className="px-16px py-12px">
        <FormattedMessage id="noPersonFound" />
      </div>
    );
  }
  if (displayMode === 'list') {
    return (
      <div className="container-fluid p-0">
        {displayedPersons.map((person) => (
          <PersonCard key={person.personId} person={person} emphasizedText={emphasizedText} />
        ))}
      </div>
    );
  }

  return (
    <Box flex="min">
      <AGGridWrapper<Person>
        identifier="person-search-results"
        columnDefs={columnDefs}
        rowData={displayedPersons}
        loading={false}
        onRowClicked={({ data }) => {
          if (isNotDefined(data?.personId)) {
            return;
          }
          dispatch(setPersonIdDisplayedInDrawer(data.personId));
        }}
        gridHeigth={600}
      />
    </Box>
  );
};

function generateColumnDefinitions($t: IntlFormatters['$t'], locale: string): ColDef<Person>[] {
  const lastNameWithBirthNameValueFormatter: ValueFormatterFunc<Person, any> = (params) =>
    params.value + (isDefined(params.data?.birthName) ? ` (${params.data?.birthName})` : '');
  const countryCellRenderer = ({ value }: CustomCellRendererProps) => {
    return isDefined(value) ? formatCountryName(value, locale) : null;
  };
  return [
    {
      field: 'personId',
      headerName: $t({ id: 'persons.table.personId' }),
      headerTooltip: $t({ id: 'persons.table.personId' }),
      minWidth: 160,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
      },

      cellClass: 'border-end',
    },
    {
      field: 'firstName',
      headerName: $t({ id: 'persons.table.firstName' }),
      headerTooltip: $t({ id: 'persons.table.firstName' }),
      minWidth: 200,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
      },
      cellClass: 'border-end',
    },
    {
      field: 'lastName',
      headerName: $t({ id: 'persons.table.lastName' }),
      headerTooltip: $t({ id: 'persons.table.lastName' }),
      minWidth: 260,
      filter: 'agTextColumnFilter',
      valueFormatter: lastNameWithBirthNameValueFormatter,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
      },
      cellClass: 'border-end',
    },
    {
      field: 'birthDate',
      headerName: $t({ id: 'persons.table.birthDate' }),
      headerTooltip: $t({ id: 'persons.table.birthDate' }),
      minWidth: 180,
      cellRenderer: ({ value }: CustomCellRendererProps) => formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
        filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
        inRangeInclusive: true,
        comparator: compareDates,
      },
      cellClass: 'border-end',
    },
    {
      field: 'residenceCountry',
      headerName: $t({ id: 'persons.table.residenceCountry' }),
      headerTooltip: $t({ id: 'persons.table.residenceCountry' }),
      minWidth: 220,
      cellRenderer: countryCellRenderer,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
        valueFormatter: countryCellRenderer,
      },
      cellClass: 'border-end',
    },
    {
      field: 'amlLevel',
      headerName: $t({ id: 'persons.table.amlLevel' }),
      headerTooltip: $t({ id: 'persons.table.amlLevel' }),
      minWidth: 220,
      cellRenderer: ({ value }: CustomCellRendererProps) => {
        return value ? <AmlLevelBadge amlLevel={value} /> : '-';
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        maxNumConditions: 1,
        valueFormatter: ({ value }: { value: string }) => {
          return $t({ id: `person.amlLevel.${value}` });
        },
      },
      cellClass: 'border-end',
    },
  ];
}
